import { ShowDialogProps } from '@lovoldsystem/ui'
import { createContext, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useGetProject } from 'src/api/project'
import { axios } from 'src/axios'
import { CenteredSpinner } from 'src/components/Spinner'
import { DialogContextType } from 'src/enums'
import { IProjectInfo } from 'src/generated-types/project'
import { DialogContainer, ShowDialogParams } from 'src/show-dialog'
import { generateUUID } from 'src/utils/function'

interface ProjectPlanningContextProps {
  permission: number
  selectedProject: IProjectInfo
  showProjectDialog: (Content: any, options: any, dialogContextType?: DialogContextType, isWithLayout?: boolean) => any
}

interface DialogContextParams<T> extends ShowDialogParams<T> {
  title?: string
}

export const ProjectPlanningCtx = createContext<ProjectPlanningContextProps>(null!)

export const ProjectPlanningContextProvider: React.FC = ({ children }) => {
  const { projectId, customerId, localityId } = useParams()
  const { data, isLoading } = useGetProject(customerId as EntityId, localityId as EntityId, projectId as EntityId)
  const [permission, setPermission] = useState<number>(-1)
  const selectedProject = useMemo(() => data as IProjectInfo, [data])
  const [dialogs, setDialogs] = useState({})

  useEffect(() => {
    if (data) {
      axios.defaults.headers['X-Permissions'] = data.encryptedPermission
      setPermission(data?.permission as number)
    }
  }, [data])

  // Need to come up with a common solution for all the Custom Dialogs
  const closeDialog = useCallback((data, id, pop, contextType = null) => {
    if (pop) {
      pop(data)
    }
    if (contextType) {
      setDialogs(dialogList => {
        const newDialogList = { ...dialogList }
        if (newDialogList[contextType]) {
          delete newDialogList[contextType][id]
          return { ...newDialogList }
        }
        return newDialogList
      })
    } else {
      setDialogs(prev => {
        const dialogList = { ...prev }
        delete dialogList[id]
        return dialogList
      })
    }
  }, [])

  const showProjectDialog = useCallback(
    function showDialogfunc<T = any, K = any>(
      Content: FC<K & ShowDialogProps<T>>,
      options: DialogContextParams<K> = {},
      contextType?: DialogContextType,
      isWithLayout?: boolean,
    ) {
      const { opts, componentProps, pop, isAutoCloseDisabled, title, sx } = options

      const id = `locality-${generateUUID()}`
      setDialogs(prevDialog => {
        return {
          ...prevDialog,
          ...{
            [id]: (
              <DialogContainer
                pop={data => closeDialog(data, id, pop)}
                content={Content}
                opts={opts}
                contentProps={componentProps as any}
                isAutoCloseDisabled={isAutoCloseDisabled}
                title={title}
                isWithLayout={isWithLayout}
                sx={sx}
              />
            ),
          },
        }
      })
    },
    [closeDialog],
  )

  const contextValue: ProjectPlanningContextProps = useMemo(
    () => ({
      selectedProject,
      permission,
      showProjectDialog,
    }),
    [selectedProject, permission, showProjectDialog],
  )

  if (isLoading) return <CenteredSpinner />

  return (
    <ProjectPlanningCtx.Provider value={contextValue}>
      {permission != -1 && (
        <>
          {children}
          {Object.values(dialogs)}
        </>
      )}
    </ProjectPlanningCtx.Provider>
  )
}
